import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import { ChecklistNode, ChecklistSection, isQuestion, Question, QuestionContainer, QuestionHeader } from "checklists";
import { Button, DialogContentText } from "@mui/material";
import { PrQuestionHeader } from "./PrQuestionHeader";
import { PrQuestion } from "./PrQuestion";
import { PrChecklistDispatchContext } from "./PrChecklist";
import { CompletionMarker } from "./CompletionMarker";
import { ConfirmationDialog } from "common/ConfirmationDialog";
import { ChecklistSectionDisplay } from "./ChecklistSectionDisplay";
import { ChecklistActionType, nodeMatches } from "./checklistReducer";
import { ChecklistNodeTreePath } from "./models";
import { makeStyles } from "../makeStyles";
import { useCurrentUser, Permissions } from "users";
import { PracticeReviewContext } from "../practice-reviews/PracticeReviewScreen";

const useStyles = makeStyles<Props>()((theme) => {
  return {
    naButton: {
      marginLeft: "auto",
      padding: "0 12px",
      fontSize: "0.75em",
      flexShrink: 0
    },
    completionMarker: {
      marginLeft: theme.spacing(3),
      position: "relative",
      left: "-0.5rem",
      flexShrink: 0
    }
  };
});

interface Props {
  section: ChecklistSection;
  tabSectionId: number;
  currentQuestionTreePath?: ChecklistNodeTreePath;
  findingInvalidAnswers: boolean;
  isFiltered?: boolean;
}

const PrChecklistSection: React.FunctionComponent<Props> = (props) => {
  const { classes } = useStyles(props);
  const { dispatch } = useContext(PrChecklistDispatchContext);
  const { practiceReview } = useContext(PracticeReviewContext);

  const [confirmingMarkAllNa, setConfirmingMarkAllNa] = useState(false);

  const allAnswersAreNa = useCallback((node: ChecklistNode): boolean => {
    if (isQuestion(node)) {
      return node.answer?.isNa ?? false;
    } else {
      return (node as QuestionContainer).children.every((c) => allAnswersAreNa(c));
    }
  }, []);

  const allAnswersInSectionAreNa = useMemo(() => allAnswersAreNa(props.section), [props.section, allAnswersAreNa]);

  const someAnswersAreNotNa = useCallback((node: ChecklistNode): boolean => {
    if (isQuestion(node)) {
      const question = node as Question;
      return (question.answer?.isYes ?? false) || (question.answer?.isReportable ?? false) || (question.answer?.isNonReportable ?? false);
    } else {
      return (node as QuestionContainer).children.some((c) => someAnswersAreNotNa(c));
    }
  }, []);

  const handleMarkAllNa = useCallback(() => {
    const someAnswersInSectionAreAnsweredAndNotNa = someAnswersAreNotNa(props.section);

    if (!someAnswersInSectionAreAnsweredAndNotNa) {
      dispatch({ type: ChecklistActionType.MarkAllNa, sectionTreePath: props.section.treePath, tabSectionId: props.tabSectionId });
    } else {
      setConfirmingMarkAllNa(true);
    }
  }, [props.section, props.tabSectionId, dispatch, someAnswersAreNotNa]);

  const dispatchToggleExpanded = useCallback(
    () =>
      dispatch({
        type: ChecklistActionType.ToggleSectionExpansion,
        sectionTreePath: props.section.treePath,
        sectionExpanded: props.section.expanded,
        tabSectionId: props.tabSectionId
      }),
    [props.section.treePath, props.section.expanded, props.tabSectionId, dispatch]
  );

  const dispatchMarkAllNa = useCallback(() => {
    dispatch({ type: ChecklistActionType.MarkAllNa, sectionTreePath: props.section.treePath, tabSectionId: props.tabSectionId });
    setConfirmingMarkAllNa(false);
  }, [props.section.treePath, props.tabSectionId, dispatch]);

  const dispatchUnmarkAllNa = useCallback(
    () => dispatch({ type: ChecklistActionType.UnmarkAllNa, sectionTreePath: props.section.treePath, tabSectionId: props.tabSectionId }),
    [props.section.treePath, props.tabSectionId, dispatch]
  );
  const { userHasPermission } = useCurrentUser();
  const canEdit = (practiceReview && !practiceReview.hasBeenReturned) || userHasPermission(Permissions.ReturnedPrUpdate);

  return (
    <ChecklistSectionDisplay
      section={props.section}
      expanded={props.section.expanded}
      toggleExpanded={dispatchToggleExpanded}
      isFiltered={props.isFiltered}
      adornments={
        <>
          {allAnswersInSectionAreNa ? (
            <Button
              variant="contained"
              className={classes.naButton}
              hidden={!canEdit}
              onClick={(e) => {
                dispatchUnmarkAllNa();
                (e.target as HTMLButtonElement).blur();
              }}>
              Unmark All N/A
            </Button>
          ) : (
            <Button
              variant="contained"
              className={classes.naButton}
              hidden={!canEdit}
              onClick={(e) => {
                handleMarkAllNa();
                (e.target as HTMLButtonElement).blur();
              }}>
              Mark All N/A
            </Button>
          )}
          <CompletionMarker section={props.section} className={classes.completionMarker} />
          {confirmingMarkAllNa && (
            <ConfirmationDialog
              open={true}
              body={<DialogContentText>Section has answers. Discard them?</DialogContentText>}
              title="Discard answers?"
              noDanger
              cancel={() => setConfirmingMarkAllNa(false)}
              confirm={dispatchMarkAllNa}
            />
          )}
        </>
      }>
      {props.section.children.map((child) => {
        switch (child.__typename) {
          case "ChecklistSection":
            return (
              <PrChecklistSection
                key={`section-${child.id}`}
                section={child as ChecklistSection}
                tabSectionId={props.tabSectionId}
                currentQuestionTreePath={
                  props.currentQuestionTreePath && nodeMatches(child, props.currentQuestionTreePath, child.depth)
                    ? props.currentQuestionTreePath
                    : undefined
                }
                findingInvalidAnswers={props.findingInvalidAnswers}
                isFiltered={props.isFiltered}
              />
            );
          case "QuestionHeader":
            return (
              <PrQuestionHeader
                key={`question-header-${child.id}`}
                questionHeader={child as QuestionHeader}
                tabSectionId={props.tabSectionId}
                currentQuestionTreePath={
                  props.currentQuestionTreePath && nodeMatches(child, props.currentQuestionTreePath, child.depth)
                    ? props.currentQuestionTreePath
                    : undefined
                }
                findingInvalidAnswers={props.findingInvalidAnswers}
              />
            );
          case "Question":
            return (
              <PrQuestion
                key={`question-${child.id}`}
                question={child as Question}
                tabSectionId={props.tabSectionId}
                isCurrentQuestion={
                  props.currentQuestionTreePath !== undefined && nodeMatches(child, props.currentQuestionTreePath, child.depth)
                }
                findingInvalidAnswers={props.findingInvalidAnswers}
              />
            );
          default:
            return null;
        }
      })}
    </ChecklistSectionDisplay>
  );
};

const MemoizedPrChecklistSection = memo(PrChecklistSection);
export { MemoizedPrChecklistSection as PrChecklistSection };
